import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import type { ParseKeys } from "i18next";
import { PlayCircle } from "lucide-react";
import type { FC } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { useAppName } from "@/hooks/use-app-name";

interface IWelcomeTourDialogProps {
  isOpen: boolean;
  onStartTour: () => void;
  onClose: () => void;
  titleKey?: ParseKeys<"tour">;
  descriptionKey?: ParseKeys<"tour">;
}

const WelcomeTourDialog: FC<IWelcomeTourDialogProps> = ({
  isOpen,
  onStartTour,
  onClose,
  titleKey = "welcome.title",
  descriptionKey = "welcome.description2",
}) => {
  const { t } = useTranslation("tour");
  const appName = useAppName();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-sm p-6 md:max-w-lg"
        data-testid="welcome-tour-dialog"
        closeButtonTestId="welcome-tour-close-button"
      >
        <Logo wrapperClassName="flex justify-center mb-2" className="h-10" />
        <DialogHeader>
          <DialogTitle
            className="text-4xl font-bold text-center mb-4"
            data-testid="welcome-tour-title"
          >
            <Trans
              t={t}
              i18nKey={titleKey}
              values={{ appName }}
              components={{
                // NOTE: NSAI-5822 — --highlighted (#A1A6FF), the designer's
                // primary for TEXT, not --primary-dark (#5722EF), which is the
                // primary for BUTTONS. Painting this span in the button colour
                // put a dark violet word on a dark modal; the two are a pair,
                // and this is the light half of it.
                Highlighted: (
                  <span className="text-highlighted whitespace-nowrap" />
                ),
              }}
            />
          </DialogTitle>
          <DialogDescription
            className="text-center text-base text-muted-foreground"
            data-testid="welcome-tour-description-2"
          >
            {t(descriptionKey)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center text-center mt-4">
          <div className="flex w-full flex-col gap-2">
            <Button
              className="flex h-12 items-center justify-center gap-2 rounded-lg text-base font-medium"
              onClick={onStartTour}
              data-testid="welcome-tour-start-button"
            >
              <PlayCircle className="h-5 w-5" />
              {t("welcome.startTour")}
            </Button>
            <Button
              variant="ghost"
              className="h-12 text-base font-medium transition-colors"
              onClick={onClose}
              data-testid="welcome-tour-maybe-later-button"
            >
              {t("welcome.maybeLater")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeTourDialog;
